<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name, position: 'top', allowOtherLeft: true }"
    :loading="isDigitizingLoading || !isFetched"
    :button-bind="{ verboseName }"
    type="menu"
    has-close-header
    unfill-width
  >
    <template #content>
      <v-row no-gutters class="px-5 pb-3" justify="center">
        <v-col cols="auto" class="pt-1" v-show="!geometryProxy">
          <data-input ref="geometry-input" v-model="drawnGeometry" v-bind="drawnGeometryInputProps" />
        </v-col>
        <v-col cols="auto" class="text-body-2" style="background-color: rgba(0,0,0,0.04)" v-show="geometryProxy">
          <v-row no-gutters justify="center" align="center" class="fill-height px-2">
            {{ $i18n.t('sidebar.automaticDigitization.areaHa', { area: geometryAreaHa }) }}
            <v-icon @click="zoomToFeature" :disabled="isDigitizingLoading" class="pl-2" size="20">mdi-magnify</v-icon>
            <v-icon
              @click="resetDrawing({ resetGeometryProxy: true, skipResultClear: true })"
              :disabled="isDigitizingLoading"
              class="pl-1"
              size="20"
              color="error"
            >
              mdi-close
            </v-icon>
          </v-row>
        </v-col>
        <v-col cols="auto" class="px-3" style="min-width: 300px">
          <data-input v-model="entryData" v-bind="entryDataInputProps" />
        </v-col>
        <v-col cols="auto" style="max-width: 36px" class="pt-1" v-if="!isAnalysisDone">
          <v-tooltip bottom color="font" :disabled="!geometryProxy || isDigitizingLoading || isAreaExceeded">
            <template #activator="{ on }">
              <span v-on="on">
                <base-button
                  class="mr-0"
                  icon="mdi-play"
                  icon-size="22px"
                  :button-style="{
                    backgroundColor: geometryProxy ? $_colors.primary : 'rgb(210, 210, 210)',
                    iconColor: 'white',
                    borderRadius: 'unset',
                  }"
                  is-icon
                  :disabled="!geometryProxy || isDigitizingLoading || isAreaExceeded"
                  @click="turnOnDigitizing"
                />
              </span>
            </template>
            {{ $i18n.t('sidebar.automaticDigitization.run') }}
          </v-tooltip>
        </v-col>
        <v-col cols="auto" style="max-width: 36px" class="pt-1 mr-2" v-if="isAnalysisDone">
          <v-tooltip bottom color="font">
            <template #activator="{ on }">
              <span v-on="on">
                <base-button
                  class="mr-0"
                  icon="mdi-cancel"
                  icon-size="22px"
                  :button-style="{
                    backgroundColor: $_colors.error,
                    iconColor: 'white',
                    borderRadius: 'unset',
                  }"
                  is-icon
                  @click="resetDrawing({ resetGeometryProxy: true })"
                />
              </span>
            </template>
            {{ $i18n.t('sidebar.automaticDigitization.reset') }}
          </v-tooltip>
        </v-col>
        <v-col cols="auto" style="max-width: 36px" class="pt-1 mr-2" v-if="isAnalysisDone">
          <v-tooltip bottom color="font">
            <template #activator="{ on }">
              <span v-on="on">
                <base-button
                  class="mr-0"
                  icon="mdi-download"
                  icon-size="22px"
                  :button-style="{
                    backgroundColor: 'white',
                    iconColor: 'font',
                    borderRadius: 'unset',
                  }"
                  style="border: 1px solid rgba(0,0,0,0.2)"
                  is-icon
                  :loading="isExportLoading"
                  @click="handleExportToShp"
                />
              </span>
            </template>
            {{ $i18n.t('sidebar.automaticDigitization.exportToShp') }}
          </v-tooltip>
        </v-col>
        <v-col cols="auto" style="max-width: 36px" class="pt-1 mr-2" v-if="isAnalysisDone">
          <v-tooltip bottom color="font">
            <template #activator="{ on }">
              <span v-on="on">
                <base-button
                  class="mr-0"
                  icon="mdi-content-copy"
                  icon-size="22px"
                  :button-style="{
                    backgroundColor: 'white',
                    iconColor: 'font',
                    borderRadius: 'unset',
                  }"
                  style="border: 1px solid rgba(0,0,0,0.2)"
                  is-icon
                  @click="copyToLayer"
                />
              </span>
            </template>
            {{ $i18n.t('sidebar.copyObjects.title') }}
          </v-tooltip>
        </v-col>
      </v-row>
      <table-buttons-copy-objects-dialog
        v-if="isCopyLoaded"
        :is-visible.sync="isCopyVisible"
        :is-loading.sync="isCopyLoading"
        input-layer-id="precize-digitizing"
        :geojson="resultGeometry"
        :geojson-copy-attributes="resultAttributes"
      />
    </template>
  </navbar-menu-tool>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import NavbarMenuTool from '@/components/NavbarMenuTool';

import { Style, Fill, Stroke, Text } from 'ol/style';

import styles from '@/mixins/map/styles';
import { classusables } from '@/assets/js/stylesDefinitions';
import { getGeodesicArea } from '@/assets/js/mapUtils';
const HECTARE_LIMIT = 100;

export default {
  name: 'TheNavbarToolsPanelPreciseDigitizing',
  mixins: [styles],
  components: {
    DataInput,
    NavbarMenuTool,
    TableButtonsCopyObjectsDialog: () => import('@/components/TableButtonsCopyObjectsDialog'),
  },
  props: {
    name: {
      type: String,
    },
    verboseName: {
      type: String,
    },
  },
  computed: {
    digitizationTypes: get('automaticDigitization/digitizationTypes'),
    isAreaExceeded() {
      return (this.geometryAreaHa ?? HECTARE_LIMIT + 1) > HECTARE_LIMIT;
    },
    drawnGeometryInputProps() {
      return {
        name: 'geometry',
        dataType: {
          name: 'geometry',
        },
        additionalGeometryStyles: {
          draw: feature => this.getLabelStyle(feature),
          modify: feature => this.getLabelStyle(feature),
        },
        geometryType: 'Extent',
        disabled: !!this.drawnGeometry || !this.isFetched,
        buttonProps: {
          translationPath: 'sidebar.automaticDigitization.selectArea',
        },
        subtitleTranslationPath: 'sidebar.automaticDigitization.maxArea',
      };
    },
    entryDataInputProps() {
      return {
        dataType: {
          name: 'select',
        },
        label: this.$i18n.t('sidebar.automaticDigitization.inputData'),
        items: this.digitizationTypes,
        disabled: !this.isFetched || this.isDigitizingLoading,
        hideDetails: true,
      };
    },
    resultAttributes() {
      return [
        { attribute: 'best_label', dataType: { name: 'text' }, label: 'best_label' },
        { attribute: 'class', dataType: { name: 'text' }, label: 'class' },
        { attribute: 'labels', dataType: { name: 'text' }, label: 'labels' },
        { attribute: 'type', dataType: { name: 'text' }, label: 'type' },
      ];
    },
  },
  data: () => ({
    currentTaskId: null,
    drawnGeometry: null,
    entryData: null,
    geometryAreaHa: null,
    geometryProxy: null,
    menuOpenWatch: undefined,
    isAnalysisDone: false,
    isCopyLoaded: false,
    isCopyVisible: false,
    isCopyLoading: false,
    isDigitizingLoading: false,
    isExportLoading: false,
    isFetched: false,
    isMenuOpen: false,
  }),
  methods: {
    getDigitizationTypes: call('automaticDigitization/getDigitizationTypes'),
    getDigitizationResults: call('automaticDigitization/getDigitizationResults'),
    exportGeojsonToShapefile: call('layers/exportGeojsonToShapefile'),
    copyToLayer() {
      this.isCopyLoaded = true;
      this.isCopyVisible = true;
    },
    getLabelStyle(feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      const geometry = modifyGeometry ? modifyGeometry : feature.getGeometry();
      const isPolygon = geometry.getType() === 'Polygon';
      const geometryArea = getGeodesicArea(geometry);
      if (isPolygon) this.geometryAreaHa = ((geometryArea ?? 0) / 10000).toFixed(2);
      return isPolygon
        ? [
            new Style({
              geometry: () => {
                return isPolygon ? geometry.getInteriorPoint() : geometry;
              },
              text: new Text({
                backgroundFill: new Fill({
                  color: 'rgba(0, 0, 0, 0.6)',
                }),
                text: [
                  this.$i18n.t('sidebar.automaticDigitization.areaHa', { area: this.geometryAreaHa }),
                  'bold 13px Calibri,sans-serif',
                  ...(this.isAreaExceeded
                    ? [
                        ` (${this.$i18n.t('sidebar.automaticDigitization.areaLimitExceededLabel')})`,
                        'italic 13px Calibri,sans-serif',
                      ]
                    : []),
                ],
                fill: new Fill({
                  color: 'white',
                }),
                placement: 'point',
                stroke: new Stroke({
                  color: 'transparent',
                  width: 0,
                }),
                offsetX: 0,
                offsetY: 0,
                padding: [5, 5, 5, 5],
              }),
            }),
          ]
        : [];
    },
    hardResetDrawing() {
      this.resetDrawing({ resetGeometryProxy: true, skipResultClear: false });
    },
    resetDrawing({ resetGeometryProxy = false, skipResultClear = false, skipTogglingOff = false } = {}) {
      if (!skipTogglingOff) this.$refs['geometry-input']?.$refs['data-input'].toggleOff();
      if (!skipResultClear) {
        this.$root.$emit('toggleCustomVectorLayer', { id: 'automatic-digitization' });
        this.isAnalysisDone = false;
      }
      if (resetGeometryProxy) {
        this.geometryProxy = null;
        this.geometryAreaHa = null;
      }
    },
    zoomToFeature() {
      this.$root.$emit('createGeomAndFitView', this.geometryProxy);
    },
    async turnOnDigitizing() {
      this.isDigitizingLoading = true;
      this.currentTaskId = await this.getDigitizationResults({
        digitizationType: this.entryData,
        payload: { geometry: this.drawnGeometry },
      });
      this.$globalHub.$emit('handleCeleryTask', {
        currentTaskId: this.currentTaskId,
        taskSuccessResultParams: { type: 'data' },
        taskStartSnackbar: { message: this.$i18n.t('snackbar.automaticDigitization') },
        taskFailureCallback: () => {
          this.$root.$emit('toggleCustomVectorLayer', { id: 'automatic-digitization' });
          this.isDigitizingLoading = false;
        },
        taskSuccessCallback: r => {
          this.resultGeometry = r;
          this.$root.$emit('toggleCustomVectorLayer', {
            id: 'automatic-digitization',
            geometry: this.resultGeometry,
            layerParams: { style: feature => [this.getFeatureStyle(feature, classusables, 'polygon', true)] },
          });
          this.isAnalysisDone = true;
          this.isDigitizingLoading = false;
          this.$refs['geometry-input']?.$refs['data-input'].toggleOff();
        },
      });
    },
    async handleExportToShp() {
      this.isExportLoading = true;
      try {
        await this.exportGeojsonToShapefile({
          ...this.resultGeometry,
          name: this.$i18n.t('sidebar.automaticDigitization.fileName'),
        });
      } finally {
        this.isExportLoading = false;
      }
    },
    async init() {
      this.menuOpenWatch();
      this.isFetched = false;
      if (!this.digitizationTypes.length) await this.getDigitizationTypes();
      this.entryData = this.digitizationTypes[0]?.value;
      this.isFetched = true;
    },
  },
  watch: {
    activeTool: {
      handler(nV) {
        if (!nV) return;
        this.resetDrawing({ skipResultClear: true });
      },
    },
    drawnGeometry: {
      handler(nV) {
        if (!nV) return;
        this.resetDrawing({ skipTogglingOff: true });
        this.geometryProxy = { ...nV };
      },
      deep: true,
    },
  },
  mounted() {
    this.menuOpenWatch = this.$watch(
      vm => vm.isMenuOpen,
      nV => {
        if (nV) this.init();
      },
      { immediate: true }
    );
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .navbar-menu-tool-top {
    min-width: unset !important;
    width: unset !important;
  }
}
</style>
